import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Award, BriefcaseBusiness, Building2, ChevronLeft, ChevronRight,
  Clock3, Filter, Pencil, Plus, RefreshCw, Search, Trash2, Users,
} from 'lucide-react';
import './Employees.css';
import { getEmployees, getDepartments, getDesignations, createEmployee, updateEmployee, deleteEmployee } from '../hrisApi';
import EmployeeFormModal from './EmployeeFormModal';
import ConfirmationModal from '../../../components/common/ConfirmationModal';

const AVATAR_TONES = ['violet', 'blue', 'orange', 'teal', 'green', 'pink', 'purple', 'sky', 'amber', 'rose'];
const LOCAL_PHOTO_PREFIX = 'onealpha_hris_employee_photo_';

const initials = (name) => (name || '').split(' ').map((part) => part[0]).filter(Boolean).slice(0, 2).join('');

const readLocalEmployeePhoto = (employeeId) => {
  if (!employeeId) return null;
  try {
    return window.localStorage.getItem(`${LOCAL_PHOTO_PREFIX}${employeeId}`);
  } catch (_) {
    return null;
  }
};

const resolveEmployeePhoto = (record = {}) => {
  const localPhoto = readLocalEmployeePhoto(record.id || record.employeeId);
  if (localPhoto) return localPhoto;
  const value = record.photoUrl || record.avatarUrl || record.profileImageUrl || record.imageUrl
    || record.photo || record.profilePicture || record.avatar || record.image;
  if (!value) return null;
  if (/^https?:\/\//i.test(String(value)) || String(value).startsWith('data:')) return value;
  const apiUrl = process.env.REACT_APP_API_ENDPOINT || 'http://localhost:8080';
  return `${apiUrl}/api/public/displayimage/${encodeURIComponent(value)}`;
};

const formatJoinDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Maps a Go HRIS Employee record onto the shape this page's UI expects.
// competencies/certs/projects/availability have no backend source yet
// (Employee doesn't track those) — defaulted rather than fabricated.
const toDirectoryRow = (record, index) => ({
  routeId: String(record.id || record.employeeId),
  recordId: record.id,
  id: record.employeeId || String(record.id),
  name: record.fullName || '—',
  email: record.email || '—',
  title: record.designation || '—',
  department: record.department || '—',
  status: record.status || 'Active',
  competencies: [],
  certs: 0,
  projects: 0,
  availability: record.status === 'Active' ? 'Available' : 'Unavailable',
  join: formatJoinDate(record.joinDate),
  initials: initials(record.fullName),
  photo: resolveEmployeePhoto(record),
  tone: AVATAR_TONES[index % AVATAR_TONES.length],
  // Raw fields kept for the Edit form (not derivable from the computed
  // fields above) — see toEmployeeRow in the Go repo for the full shape.
  raw: record,
});

function Avatar({ employee, size = 'normal' }) {
  return (
    <span className={`employee-avatar ${employee.tone} ${employee.photo ? 'has-photo' : ''} ${size}`}>
      <span>{employee.initials || initials(employee.name)}</span>
      {employee.photo && (
        <img
          src={employee.photo}
          alt={employee.name}
          onError={(event) => {
            event.currentTarget.style.display = 'none';
            event.currentTarget.parentElement.classList.remove('has-photo');
          }}
        />
      )}
    </span>
  );
}

function Status({ value }) {
  return <span className={`employee-status ${value.toLowerCase().replace(' ', '-')}`}><i />{value}</span>;
}

const Employees = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [department, setDepartment] = useState('All departments');
  const [title, setTitle] = useState('All job titles');
  const [competency, setCompetency] = useState('All competencies');
  const [status, setStatus] = useState('All statuses');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(7);
  const [showFilters, setShowFilters] = useState(true);

  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [jobTitleOptions, setJobTitleOptions] = useState([]);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingEmployee, setDeletingEmployee] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [actionError, setActionError] = useState('');

  const load = () => {
    setLoading(true);
    setError(null);
    getEmployees({ page: 0, size: 500 })
      .then((result) => {
        const rows = (result.content || []).map(toDirectoryRow);
        setEmployees(rows);
      })
      .catch(() => setError('Could not load employees from the server.'))
      .finally(() => setLoading(false));
  };

  const loadOptions = () => {
    getDepartments({ page: 0, size: 500 }).then((result) => setDepartmentOptions(result.content || [])).catch(() => setDepartmentOptions([]));
    getDesignations({ page: 0, size: 500 }).then((result) => setJobTitleOptions(result.content || [])).catch(() => setJobTitleOptions([]));
  };

  useEffect(() => { load(); loadOptions(); }, []);

  const openAddModal = () => { setEditingEmployee(null); setActionError(''); setShowFormModal(true); };
  const openEditModal = (employee) => { setEditingEmployee(employee.raw); setActionError(''); setShowFormModal(true); };

  const handleSaveEmployee = async (formValues) => {
    setSaving(true);
    try {
      if (editingEmployee) {
        await updateEmployee(editingEmployee.id, formValues);
      } else {
        await createEmployee(formValues);
      }
      setShowFormModal(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEmployee = async () => {
    if (!deletingEmployee) return;
    setDeleting(true);
    setActionError('');
    try {
      await deleteEmployee(deletingEmployee.recordId);
      setDeletingEmployee(null);
      load();
    } catch (err) {
      setActionError(err?.response?.data?.message || err?.message || 'Gagal menghapus employee.');
    } finally {
      setDeleting(false);
    }
  };

  const unique = (key) => [...new Set(employees.map((employee) => employee[key]).filter(Boolean))];

  const filtered = useMemo(() => employees.filter((employee) => {
    const haystack = `${employee.name} ${employee.id} ${employee.email} ${employee.department} ${employee.title}`.toLowerCase();
    return (!query || haystack.includes(query.toLowerCase()))
      && (department === 'All departments' || employee.department === department)
      && (title === 'All job titles' || employee.title === title)
      && (competency === 'All competencies' || employee.competencies.includes(competency))
      && (status === 'All statuses' || employee.status === status);
  }), [employees, query, department, title, competency, status]);
  const visible = filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const reset = () => { setQuery(''); setDepartment('All departments'); setTitle('All job titles'); setCompetency('All competencies'); setStatus('All statuses'); setPage(1); };
  const activeFilters = [department !== 'All departments', title !== 'All job titles', competency !== 'All competencies', status !== 'All statuses'].filter(Boolean).length;
  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const select = (event, setter) => { setter(event.target.value); setPage(1); };
  const changeRowsPerPage = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };
  const openDetail = (employee) => navigate(`/hris/employees/${employee.routeId}`);

  return <div className="employee-directory-page">
    <header className="employee-page-header"><div><div className="eyebrow"><Users size={15} /> HUMAN RESOURCES</div><h1>Employees</h1><p>Directory karyawan untuk melihat jabatan, department, kompetensi, dan kesiapan penugasan.</p></div><div className="d-flex align-items-center gap-3"><div className="source-state"><span><i /> {loading ? 'Loading…' : error ? 'Offline' : 'Live data'}</span><small>{error || 'Synced from HRIS backend'}</small></div><button type="button" className="btn btn-primary d-flex align-items-center gap-1" onClick={openAddModal}><Plus size={16} /> Add Employee</button></div></header>
    {actionError && <div className="alert alert-danger">{actionError}</div>}
    <section className="employee-summary-grid"><article><span className="summary-icon teal"><Users size={21} /></span><div><small>Total employees</small><strong>{employees.length}</strong></div></article><article><span className="summary-icon blue"><Building2 size={21} /></span><div><small>Departments</small><strong>{unique('department').length}</strong></div></article><article><span className="summary-icon purple"><Award size={21} /></span><div><small>With competencies</small><strong>{employees.filter((employee) => employee.competencies.length).length}</strong></div></article><article><span className="summary-icon orange"><BriefcaseBusiness size={21} /></span><div><small>Available for assignment</small><strong>{employees.filter((employee) => employee.availability === 'Available').length}</strong></div></article></section>
    <section className="employee-filter-panel"><div className="filter-panel-head"><div><h2>Employee directory</h2><p>Gunakan pencarian dan filter untuk menemukan personel yang sesuai.</p></div><button className="filter-toggle" onClick={() => setShowFilters((value) => !value)}><Filter size={16} /> Filters {activeFilters > 0 && <b>{activeFilters}</b>}</button></div><div className="search-row"><label className="employee-search"><Search size={18} /><input value={query} onChange={(event) => { setQuery(event.target.value); setPage(1); }} placeholder="Search name, employee ID, email, job title..." /></label><button className="clear-filter" onClick={reset}>Clear</button><button className="refresh-button" onClick={load} title="Refresh from server"><RefreshCw size={17} /> Refresh</button></div>{showFilters && <div className="filter-row"><label>Department<select value={department} onChange={(event) => select(event, setDepartment)}><option>All departments</option>{unique('department').map((value) => <option key={value}>{value}</option>)}</select></label><label>Job title<select value={title} onChange={(event) => select(event, setTitle)}><option>All job titles</option>{unique('title').map((value) => <option key={value}>{value}</option>)}</select></label><label>Competency<select value={competency} onChange={(event) => select(event, setCompetency)}><option>All competencies</option>{[...new Set(employees.flatMap((employee) => employee.competencies))].sort().map((value) => <option key={value}>{value}</option>)}</select></label><label>Status<select value={status} onChange={(event) => select(event, setStatus)}><option>All statuses</option>{unique('status').map((value) => <option key={value}>{value}</option>)}</select></label></div>}</section>
    <section className="employee-table-panel"><div className="directory-heading"><div><h2>Employees</h2><p>{filtered.length} employee{filtered.length === 1 ? '' : 's'} shown</p></div><span className="sync-note"><Clock3 size={15} /> Synced from HRIS backend</span></div><div className="table-scroll"><table className="employee-table"><thead><tr><th>Employee</th><th>Job title</th><th>Department</th><th>Competencies</th><th>Assignments</th><th>Status</th><th aria-label="Action" /></tr></thead><tbody>{visible.length ? visible.map((employee) => <tr key={employee.routeId} onClick={() => openDetail(employee)}><td><div className="name-cell"><Avatar employee={employee} /><span><strong>{employee.name}</strong><small>{employee.id} · {employee.email}</small></span></div></td><td>{employee.title}</td><td><span className="department-pill">{employee.department}</span></td><td><div className="competency-tags">{employee.competencies.slice(0, 2).map((value) => <span key={value}>{value}</span>)}{employee.competencies.length > 2 && <span className="more">+{employee.competencies.length - 2}</span>}</div></td><td><span className="assignment-count"><BriefcaseBusiness size={15} /> {employee.projects} active</span></td><td><Status value={employee.status} /></td><td className="text-nowrap"><button className="row-action" onClick={(event) => { event.stopPropagation(); openDetail(employee); }}>View</button> <button className="btn btn-icon btn-sm btn-outline-secondary" title="Edit" onClick={(event) => { event.stopPropagation(); openEditModal(employee); }}><Pencil size={14} /></button> <button className="btn btn-icon btn-sm btn-outline-danger" title="Delete" onClick={(event) => { event.stopPropagation(); setActionError(''); setDeletingEmployee(employee); }}><Trash2 size={14} /></button></td></tr>) : <tr><td colSpan="7" className="empty-state">{loading ? 'Loading employees…' : 'Tidak ada karyawan yang sesuai dengan filter.'}</td></tr>}</tbody></table></div><footer className="table-footer"><span>Showing {filtered.length ? ((page - 1) * rowsPerPage) + 1 : 0}-{Math.min(page * rowsPerPage, filtered.length)} of {filtered.length} employees</span><div className="pagination-controls"><label>Rows per page<select value={rowsPerPage} onChange={changeRowsPerPage}><option value={5}>5</option><option value={7}>7</option><option value={10}>10</option><option value={25}>25</option></select></label><div className="pager"><button disabled={page === 1} onClick={() => setPage((value) => value - 1)} aria-label="Previous page"><ChevronLeft size={18} /></button><b>{page}</b><button disabled={page === totalPages} onClick={() => setPage((value) => value + 1)} aria-label="Next page"><ChevronRight size={18} /></button></div></div></footer></section>
    <EmployeeFormModal
      isOpen={showFormModal}
      onClose={() => setShowFormModal(false)}
      onSave={handleSaveEmployee}
      employee={editingEmployee}
      departments={departmentOptions}
      jobTitles={jobTitleOptions}
      saving={saving}
    />
    <ConfirmationModal
      isOpen={Boolean(deletingEmployee)}
      onClose={() => setDeletingEmployee(null)}
      onConfirm={handleDeleteEmployee}
      title="Delete Employee"
      message={`Hapus employee "${deletingEmployee?.name || ''}" dari Datahub? Tindakan ini tidak dapat dibatalkan.`}
      confirmText="Delete"
      confirmVariant="danger"
      isLoading={deleting}
    />
  </div>;
};

export default Employees;
